import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiUser, FiFileText, FiTrendingUp, FiCalendar, FiPlus, FiSearch, FiFilter, FiEdit, FiTrash2, FiPrinter, FiX } from 'react-icons/fi';
import { FaHandshake, FaCar, FaChartLine } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import DealBuilder from './DealBuilder';
import DealJacket from './DealJacket';
import DealFormsPanel from './DealFormsPanel';
import { modelFromDealRow } from '../../config/dealPacketForms';

const DealManagement = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [deals, setDeals] = useState([]);
  const [packetDeal, setPacketDeal] = useState(null);
  const packetModel = useMemo(() => (packetDeal ? modelFromDealRow(packetDeal) : null), [packetDeal]);
  const [stats, setStats] = useState({
    totalDeals: 0,
    pendingDeals: 0,
    completedDeals: 0,
    totalRevenue: 0,
    avgGrossProfit: 0,
    monthlyRevenue: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDealJacket, setShowDealJacket] = useState(false);
  const [dealJacketData, setDealJacketData] = useState(null);

  useEffect(() => {
    loadDeals();
  }, [filterStatus]);

  const loadDeals = async () => {
    try {
      setLoading(true);
      let query = supabase.from('deals').select(`
        *,
        vehicles_db2025!vehicle_id (year, make, model, vin)
      `).order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;
      if (error) throw error;

      setDeals(data || []);
      calculateStats(data || []);
    } catch (err) {
      console.error('Error loading deals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDeal = (deal) => {
    setSelectedDeal(deal);
    setActiveView('builder');
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', dealId);

      if (error) throw error;

      // Remove from local state immediately
      setDeals(prevDeals => prevDeals.filter(deal => deal.id !== dealId));
      
      // Recalculate stats
      const updatedDeals = deals.filter(deal => deal.id !== dealId);
      calculateStats(updatedDeals);
      
      alert('Deal deleted successfully!');
    } catch (err) {
      console.error('Error deleting deal:', err);
      alert('Failed to delete deal: ' + err.message);
      // Reload in case of error to ensure consistency
      loadDeals();
    }
  };

  const calculateStats = (dealData) => {
    const stats = {
      totalDeals: dealData.length,
      pendingDeals: dealData.filter(d => d.status === 'working' || d.status === 'pending_finance').length,
      completedDeals: dealData.filter(d => d.status === 'contracted' || d.status === 'funded').length,
      totalRevenue: dealData.reduce((sum, d) => sum + (d.total_sale_price || 0), 0),
      avgGrossProfit: dealData.length > 0 
        ? dealData.reduce((sum, d) => sum + (d.total_gross || 0), 0) / dealData.length 
        : 0,
      monthlyRevenue: dealData
        .filter(d => new Date(d.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        .reduce((sum, d) => sum + (d.total_sale_price || 0), 0)
    };
    setStats(stats);
  };

  const getStatusColor = (status) => {
    const colors = {
      working: 'bg-blue-100 text-blue-800',
      pending_finance: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      contracted: 'bg-purple-100 text-purple-800',
      funded: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredDeals = deals.filter(deal => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      deal.deal_number?.toLowerCase().includes(search) ||
      deal.customers?.first_name?.toLowerCase().includes(search) ||
      deal.customers?.last_name?.toLowerCase().includes(search) ||
      deal.vehicles?.make?.toLowerCase().includes(search) ||
      deal.vehicles?.model?.toLowerCase().includes(search)
    );
  });

  if (activeView === 'builder') {
    return (
      <div>
        <button
          onClick={() => setActiveView('dashboard')}
          className="mb-4 px-4 py-2 text-gray-600 hover:text-gray-900"
        >
          ← Back to Dashboard
        </button>
        <DealBuilder existingDeal={selectedDeal} />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Deal Management</h2>
          <p className="text-gray-600">Manage and track all vehicle deals</p>
        </div>
        <button
          onClick={() => setActiveView('builder')}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold flex items-center gap-2"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          New Deal
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Total Deals</p>
              <p className="text-xl font-bold text-gray-900">{stats.totalDeals}</p>
            </div>
            <SafeIcon icon={FaHandshake} className="w-8 h-8 text-primary-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Pending</p>
              <p className="text-xl font-bold text-yellow-600">{stats.pendingDeals}</p>
            </div>
            <SafeIcon icon={FiCalendar} className="w-8 h-8 text-yellow-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Completed</p>
              <p className="text-xl font-bold text-green-600">{stats.completedDeals}</p>
            </div>
            <SafeIcon icon={FiFileText} className="w-8 h-8 text-green-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Total Revenue</p>
              <p className="text-xl font-bold text-gray-900">${(stats.totalRevenue / 1000).toFixed(0)}k</p>
            </div>
            <SafeIcon icon={FiDollarSign} className="w-8 h-8 text-green-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Avg Gross</p>
              <p className="text-xl font-bold text-gray-900">${stats.avgGrossProfit.toLocaleString()}</p>
            </div>
            <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-blue-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">30-Day Revenue</p>
              <p className="text-xl font-bold text-gray-900">${(stats.monthlyRevenue / 1000).toFixed(0)}k</p>
            </div>
            <SafeIcon icon={FaChartLine} className="w-8 h-8 text-purple-500" />
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by customer, vehicle, or deal number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-2">
            {['all', 'working', 'pending_finance', 'contracted', 'funded'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === status 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Deals Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No deals found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gross</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDeals.map((deal) => (
                  <tr key={deal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{deal.id?.slice(0, 8) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {deal.customer_name || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">{deal.customer_phone || 'No phone'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {deal.vehicles_db2025 ? (
                        <div>
                          {deal.vehicles_db2025.year} {deal.vehicles_db2025.make} {deal.vehicles_db2025.model}
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${deal.total_price?.toLocaleString() || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${deal.monthly_payment?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${deal.gross_profit?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(deal.status)}`}>
                        {deal.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setPacketDeal(deal)}
                          className="text-primary-600 hover:text-primary-700"
                          title="Deal forms packet (auto-filled)"
                        >
                          <SafeIcon icon={FiPrinter} className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            setDealJacketData(deal);
                            setShowDealJacket(true);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                          title="View Documents"
                        >
                          <SafeIcon icon={FiFileText} className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleEditDeal(deal)}
                          className="text-primary-600 hover:text-primary-700"
                          title="Edit deal"
                        >
                          <SafeIcon icon={FiEdit} className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteDeal(deal.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete deal"
                        >
                          <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Deal Jacket Modal */}
      {showDealJacket && dealJacketData && (
        <DealJacket
          dealId={dealJacketData.id}
          dealData={dealJacketData}
          onClose={() => {
            setShowDealJacket(false);
            setDealJacketData(null);
          }}
        />
      )}

      {/* Auto-filled deal packet modal */}
      {packetDeal && packetModel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-screen overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-semibold">Deal Packet — {packetDeal.customer_name || 'Deal'}</h2>
                <p className="text-sm text-gray-500">
                  {packetDeal.vehicles_db2025
                    ? `${packetDeal.vehicles_db2025.year} ${packetDeal.vehicles_db2025.make} ${packetDeal.vehicles_db2025.model}`
                    : 'No vehicle linked'}
                </p>
              </div>
              <button
                onClick={() => setPacketDeal(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" /> Close
              </button>
            </div>
            <div className="p-6">
              <DealFormsPanel model={packetModel} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealManagement;
